<template>
  <section v-if="info" class="workout-recovery mb-4" aria-label="Offenes Training">
    <button type="button" class="recovery-notice" aria-haspopup="dialog" @click="open">
      <span class="recovery-notice-copy">
        <span class="recovery-eyebrow">{{ pendingSave ? 'Speicherung noch offen' : 'Offenes Training' }}</span>
        <strong>Dein Training wartet auf dich</strong>
        <span class="recovery-plan-name">{{ info.title }}</span>
        <span class="recovery-notice-meta">{{ dateLabel }} <span aria-hidden="true">·</span> {{ setLabel }}</span>
      </span>
      <span class="recovery-notice-action">
        {{ pendingSave ? 'Speicherung prüfen' : 'Öffnen' }} <v-icon color="#a8efca" size="19">mdi-arrow-right</v-icon>
      </span>
    </button>

    <v-dialog v-model="dialog" max-width="420" dark content-class="recovery-dialog-overlay" :persistent="busy">
      <v-card class="recovery-dialog" role="document" aria-labelledby="recovery-dialog-title">
        <v-btn icon :disabled="busy" aria-label="Hinweis schliessen" class="recovery-close" width="40" height="40" @click="dialog = false"><v-icon size="22">mdi-close</v-icon></v-btn>
        <v-avatar size="80" class="recovery-avatar" :style="{ borderColor: confirmDiscard ? '#edb0a8' : workoutVisual.color }">
          <v-icon v-if="confirmDiscard" size="38" color="#edb0a8">mdi-delete-outline</v-icon>
          <v-icon v-else-if="pendingSave" size="38" color="white">mdi-content-save-outline</v-icon>
          <v-icon v-else-if="workoutVisual.icon" size="44" color="white">{{ workoutVisual.icon }}</v-icon>
          <v-img v-else :src="workoutVisual.img" contain :alt="info.title" />
        </v-avatar>
        <template v-if="confirmDiscard">
          <h2 id="recovery-dialog-title" class="recovery-title">Wirklich verwerfen?</h2>
          <p class="recovery-description">Deine erfassten Sätze werden von diesem Gerät entfernt. Das kannst du nicht rückgängig machen.</p>
        </template>
        <template v-else>
          <h2 id="recovery-dialog-title" class="recovery-title">{{ pendingSave ? 'Speicherung noch offen' : 'Training aktiv!' }}</h2>
          <p class="recovery-description">{{ pendingSave ? 'Die Speicherung wurde noch nicht bestätigt. Bitte prüfe den Status.' : 'Möchtest du dein Training fortsetzen?' }}</p>
        </template>

        <div class="recovery-workout">
          <h3>{{ info.title }}</h3>
          <div class="recovery-workout-facts">
            <span>{{ dateLabel }}</span>
            <span aria-hidden="true">·</span>
            <span>{{ setLabel }} erfasst</span>
          </div>
        </div>

        <div class="recovery-actions">
          <template v-if="confirmDiscard">
            <v-btn block height="52" depressed color="#edb0a8" class="recovery-primary" :loading="busy" @click="discard">Training verwerfen</v-btn>
            <v-btn block height="44" text class="recovery-secondary" :disabled="busy" @click="confirmDiscard = false">Training behalten</v-btn>
          </template>
          <template v-else>
            <v-btn v-if="pendingSave" block height="54" depressed color="#52ae32" class="recovery-primary" :loading="busy" @click="review">
              Speicherung prüfen
            </v-btn>
            <v-btn v-else block height="54" depressed color="#52ae32" class="recovery-primary" :loading="busy" @click="resume">
              Fortsetzen
            </v-btn>
            <v-btn v-if="!pendingSave" block height="44" text class="recovery-secondary" :disabled="busy" @click="review">
              Training abschliessen
            </v-btn>
            <div class="recovery-quiet-actions">
              <v-btn text height="44" :disabled="busy" @click="dialog = false">Später</v-btn>
              <v-btn v-if="!pendingSave" text height="44" :disabled="busy" @click="confirmDiscard = true">Verwerfen</v-btn>
            </div>
          </template>
        </div>
        <v-alert v-if="error" dense text type="error" role="alert" class="mt-3 mb-0">{{ error }}</v-alert>
      </v-card>
    </v-dialog>
  </section>
</template>

<script>
import moment from "moment"
import userService from "@/services/userService"
import trainingLiveService from "@/services/trainingLiveService"
import { workoutRecoveryInfo } from "@/services/workoutRecovery"

export default {
  name: "WorkoutRecoveryNotice",
  data: () => ({ now: Date.now(), ready: false, dialog: false, forceReview: false, confirmDiscard: false, busy: false, error: "", interval: null }),
  computed: {
    state() { return this.$store.state.runWorkoutStore },
    info() {
      // Depend on the authenticated store as well as localStorage's user identity.
      const user = this.$store.state.userStore.user
      const requested = this.$route.query.recover === this.state.liveSession?.clientSessionId && Boolean(this.$route.query.recover)
      return this.ready && user ? workoutRecoveryInfo(this.state, userService.getUserId(), this.now, this.forceReview || requested) : null
    },
    dateLabel() { return this.info?.startedAt ? moment(this.info.startedAt).format("D. MMMM YYYY") : "Datum bitte prüfen" },
    setLabel() { return this.info?.sets === 1 ? "1 Satz" : `${this.info?.sets || 0} Sätze` },
    pendingSave() { return Boolean(this.state.liveSession?.pendingSave) },
    workoutVisual() {
      const split = this.state.activeSplit || {}
      if (split.fixedVisualization?.img) return { color: "#52ae32", ...split.fixedVisualization }
      const title = String(split.title || "").toLowerCase()
      if (title.includes("hyrox")) return { icon: "mdi-lightning-bolt", color: "#ffed00" }
      const visuals = [
        [["bein", "unterkörper"], "legSplit.png", "#1C4966"],
        [["brust", "push"], "chestSplit.png", "#02734A"],
        [["rücken", "pull", "schulter"], "backSplit.png", "#F2B950"],
        [["arm", "bizeps", "trizeps"], "armSplit.png", "#E5735D"],
        [["bauch", "core", "ober"], "cardioSplit.png", "#F7B5A7"],
      ]
      const match = visuals.find(([keywords]) => keywords.some(keyword => title.includes(keyword)))
      return match ? { img: `/img/workout/${match[1]}`, color: match[2] } : { img: "/img/workout/upperBodySplit.png", color: "#A3C6C4" }
    },
  },
  watch: {
    dialog(value) { if (!value) this.forceReview = false },
    info(value) {
      if (!value) this.dialog = false
      else this.offerReminder()
    },
    "$route.query.recover"() { this.refresh() },
  },
  methods: {
    refresh() {
      if (document.visibilityState === "hidden" || !this.ready) return
      trainingLiveService.ensureSession()
      this.now = Date.now()
      this.offerReminder()
    },
    offerReminder() {
      if (!this.info || this.dialog || this.busy) return
      const requested = this.$route.query.recover === this.info.clientSessionId
      const promptedAt = this.state.liveSession?.recoveryPromptedAt
      if (requested || !promptedAt || !moment(promptedAt).isSame(this.now, "day")) this.open(requested)
      if (requested) {
        const query = { ...this.$route.query }
        delete query.recover
        this.$router.replace({ query }).catch(() => {})
      }
    },
    open(force = false) {
      this.forceReview = force === true
      if (!this.info) return
      this.confirmDiscard = false
      this.error = ""
      this.dialog = true
      this.$store.commit("runWorkoutStore/SET_LIVE_SESSION", { recoveryPromptedAt: Date.now() })
      return true
    },
    async navigate(target) {
      this.busy = true
      this.error = ""
      try {
        await this.$router.push(target)
        this.dialog = false
      } catch (_) {
        this.error = "Die Ansicht konnte nicht geöffnet werden. Bitte erneut versuchen."
      } finally { this.busy = false }
    },
    review() {
      trainingLiveService.prepareRecovery()
      return this.navigate({ name: "WorkoutSummary" })
    },
    resume() {
      if (trainingLiveService.resumeRecovered()) return this.navigate({ name: "Workout_run" })
      trainingLiveService.prepareRecovery()
      return this.navigate({ name: "WorkoutSummary", query: { resume: "true" } })
    },
    async discard() {
      if (this.busy || !this.info || this.pendingSave) return
      this.busy = true
      this.error = ""
      try {
        await this.$store.dispatch("runWorkoutStore/resetWorkoutState")
        this.dialog = false
      } catch (_) {
        this.error = "Das Training konnte nicht entfernt werden. Bitte erneut versuchen."
      } finally { this.busy = false }
    },
  },
  async mounted() {
    await this.$store.restored
    if (this._isDestroyed) return
    this.ready = true
    this.refresh()
    this.interval = setInterval(this.refresh, 60000)
    document.addEventListener("visibilitychange", this.refresh)
    window.addEventListener("trainingLiveForeground", this.refresh)
  },
  beforeDestroy() {
    clearInterval(this.interval)
    document.removeEventListener("visibilitychange", this.refresh)
    window.removeEventListener("trainingLiveForeground", this.refresh)
  },
}
</script>

<style scoped>
.recovery-notice {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 16px;
  width: 100%;
  padding: 22px;
  color: #f3f8f5;
  text-align: left;
  border: 1px solid #35463e;
  border-radius: 22px;
  background: linear-gradient(115deg, #202e28, #1b2220 75%);
  box-shadow: 0 6px 22px rgba(0, 0, 0, .12);
  transition: border-color .18s, background-color .18s;
}
.recovery-notice:focus-visible, .recovery-dialog .v-btn:focus-visible {
  outline: 3px solid #8ce7bb;
  outline-offset: 3px;
}
.recovery-notice-copy { display: flex; min-width: 0; flex-direction: column; gap: 4px; }
.recovery-eyebrow { color: #a8cfb9; font-size: 11px; line-height: 1.5; letter-spacing: .09em; font-weight: 700; text-transform: uppercase; }
.recovery-notice-copy strong { font-size: 18px; line-height: 1.35; font-weight: 700; }
.recovery-plan-name { color: #dce5df; font-size: 14px; overflow-wrap: anywhere; }
.recovery-notice-meta { display: flex; flex-wrap: wrap; gap: 6px; color: #aabbb1; font-size: 12px; line-height: 1.6; }
.recovery-notice-action { display: flex; align-items: center; gap: 10px; color: #a8efca; font-size: 14px; font-weight: 700; }
.recovery-dialog {
  position: relative;
  padding: 28px 24px 18px;
  text-align: center;
  border: 1px solid rgba(255, 255, 255, .1);
  border-radius: 24px !important;
  background: #2a2a2a !important;
  color: #fff;
  box-shadow: 0 10px 40px -10px rgba(0, 0, 0, .5);
}
.recovery-dialog .recovery-close { position: absolute; top: 8px; right: 8px; color: #d0d0d0 !important; }
.recovery-avatar { margin: 6px auto 18px; border: 3px solid; background: rgba(0, 0, 0, .15); box-shadow: 0 5px 14px rgba(0, 0, 0, .25); }
.recovery-title { margin: 0 0 8px; font-size: 24px; line-height: 1.25; font-weight: 700; }
.recovery-description { margin-bottom: 18px; color: #bdbdbd; font-size: 14px; line-height: 1.5; }
.recovery-workout { margin-bottom: 24px; }
.recovery-workout h3 { margin-bottom: 6px; font-size: 17px; line-height: 1.4; overflow-wrap: anywhere; }
.recovery-workout-facts { display: flex; justify-content: center; flex-wrap: wrap; gap: 4px 8px; color: #aaa; font-size: 12px; }
.recovery-actions { display: grid; gap: 4px; }
.recovery-dialog .v-btn:not(.v-btn--icon) { border-radius: 28px; text-transform: none; letter-spacing: 0; font-size: 14px; font-weight: 600; }
.recovery-dialog .recovery-primary { color: #13251c !important; font-size: 17px !important; font-weight: 700 !important; }
.recovery-dialog .recovery-secondary { color: #d0d0d0; }
.recovery-quiet-actions { display: flex; align-items: center; justify-content: center; gap: 8px; }
.recovery-quiet-actions .v-btn { color: #aaa; }
@media (hover: hover) {
  .recovery-notice:hover { border-color: #64977b; }
}
@media (max-width: 480px) {
  .recovery-notice { grid-template-columns: minmax(0, 1fr); gap: 12px; padding: 18px; }
  .recovery-notice-copy strong { font-size: 17px; }
  .recovery-notice-action { grid-column: 1; justify-content: space-between; padding-top: 9px; border-top: 1px solid #35463e; }
  .recovery-dialog { padding: 24px 20px 16px; }
}
@media (prefers-reduced-motion: reduce) {
  .recovery-notice { transition: none; }
}
</style>

<style>
.v-dialog.recovery-dialog-overlay { margin: 16px; width: calc(100% - 32px); border-radius: 24px; }
</style>
